// Compliance Service Management Types
// Based on backend API: /api/v1/compliance-services, /api/v1/party-subscriptions
// Refactored: ReturnType, PartyReturnTypeConfig, and ReturnGenerationRule eliminated.
// ComplianceService is now an org-level template linked to Item via item_id with scheduling fields.
// PartyServiceSubscription now has item_id and all scheduling fields directly on it.

// ============== Compliance Service ==============

export type ComplianceCategory = 'indirect_tax' | 'direct_tax' | 'compliance';
export type ReturnFrequency = 'monthly' | 'quarterly' | 'yearly' | 'on_demand';

export interface ComplianceService {
  id: number;
  organization_id: number;
  name: string;
  code: string;
  description: string | null;
  category: ComplianceCategory;
  portal_url: string | null;
  is_active: boolean;
  sort_order: number;
  // Item link
  item_id: number | null;
  // Scheduling fields
  frequency: ReturnFrequency | null;
  due_day_of_month: number | null;
  due_days_after_period: number | null;
  applicable_months: number[] | null;
  period_format: string | null;
  generate_days_before_due: number;
  auto_assign_to_subscription_user: boolean;
  default_assigned_user_id: number | null;
  default_team_id: number | null;
  task_title_template: string | null;
  default_priority: string;
  // Denormalized item fields from API response
  item_name?: string;
  item_code?: string;
  item_hsn_code?: string;
  default_team_name?: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ComplianceServiceCreate {
  name: string;
  code: string;
  description?: string | null;
  category: ComplianceCategory;
  portal_url?: string | null;
  is_active?: boolean;
  sort_order?: number;
  // Item link
  item_id?: number | null;
  // Scheduling fields
  frequency?: string | null;
  due_day_of_month?: number | null;
  due_days_after_period?: number | null;
  applicable_months?: number[] | null;
  period_format?: string | null;
  generate_days_before_due?: number;
  auto_assign_to_subscription_user?: boolean;
  default_assigned_user_id?: number | null;
  default_team_id?: number | null;
  task_title_template?: string | null;
  default_priority?: string;
}

export interface ComplianceServiceUpdate {
  name?: string;
  description?: string | null;
  category?: ComplianceCategory;
  portal_url?: string | null;
  is_active?: boolean;
  sort_order?: number;
  // Item link
  item_id?: number | null;
  // Scheduling fields
  frequency?: string | null;
  due_day_of_month?: number | null;
  due_days_after_period?: number | null;
  applicable_months?: number[] | null;
  period_format?: string | null;
  generate_days_before_due?: number;
  auto_assign_to_subscription_user?: boolean;
  default_assigned_user_id?: number | null;
  default_team_id?: number | null;
  task_title_template?: string | null;
  default_priority?: string;
}

// ============== Party Subscription ==============

export type SubscriptionStatus = 'active' | 'paused' | 'cancelled';
export type BillingType = 'per_return' | 'monthly' | 'yearly' | 'retainer';

export interface PartySubscription {
  id: number;
  organization_id: number;
  party_id: number;
  item_id: number;
  compliance_service_id: number | null;
  start_date: string;
  end_date: string | null;
  status: SubscriptionStatus;
  billing_type: BillingType;
  fee_amount: string;
  assigned_user_id: number | null;
  team_id: number | null;
  notes: string | null;
  // Scheduling fields (overrides from compliance service template)
  frequency: ReturnFrequency | null;
  due_day_of_month: number | null;
  due_days_after_period: number | null;
  applicable_months: number[] | null;
  period_format: string | null;
  generate_days_before_due: number | null;
  task_title_template: string | null;
  portal_url: string | null;
  // Denormalized fields from API response
  party_name?: string;
  item_name?: string;
  item_code?: string;
  compliance_service_name?: string;
  compliance_service_code?: string;
  assigned_user_name?: string | null;
  team_name?: string | null;
  created_at: string;
  created_by?: number;
  updated_at?: string | null;
}

export interface PartySubscriptionCreate {
  party_id: number;
  item_id: number;
  compliance_service_id?: number | null;
  start_date: string;
  end_date?: string | null;
  status?: SubscriptionStatus;
  billing_type: BillingType;
  fee_amount: string | number;
  assigned_user_id?: number | null;
  team_id?: number | null;
  notes?: string | null;
  // Scheduling override fields
  frequency?: string | null;
  due_day_of_month?: number | null;
  due_days_after_period?: number | null;
  applicable_months?: number[] | null;
  period_format?: string | null;
  generate_days_before_due?: number;
  task_title_template?: string | null;
  portal_url?: string | null;
}

export interface PartySubscriptionUpdate {
  start_date?: string;
  end_date?: string | null;
  status?: SubscriptionStatus;
  billing_type?: BillingType;
  fee_amount?: string | number;
  assigned_user_id?: number | null;
  team_id?: number | null;
  notes?: string | null;
  // Scheduling override fields
  frequency?: string | null;
  due_day_of_month?: number | null;
  due_days_after_period?: number | null;
  applicable_months?: number[] | null;
  period_format?: string | null;
  generate_days_before_due?: number;
  task_title_template?: string | null;
  portal_url?: string | null;
}

// ============== Return Generation ==============

export interface ReturnGenerationPreviewItem {
  action: 'would_create' | 'skipped';
  party: string;
  party_id: number;
  return_type: string;
  period: string;
  due_date: string;
  title?: string;
  assigned_user_id?: number | null;
  fee_amount?: string;
  reason?: string;
  existing_task_id?: number;
}

export interface ReturnGenerationPreview {
  returns_created: number;
  returns_skipped: number;
  errors: string[];
  details: ReturnGenerationPreviewItem[];
}

export interface ReturnGenerationRunRequest {
  target_date?: string;
  dry_run?: boolean;
}

export interface ReturnGenerationRunResponse {
  returns_created: number;
  returns_skipped: number;
  errors: string[];
  details: Array<{
    action: 'created' | 'skipped';
    party: string;
    party_id: number;
    return_type: string;
    period: string;
    due_date: string;
    task_id?: number;
    title?: string;
    reason?: string;
  }>;
}

export interface ReturnGenerationDashboard {
  pending_count: number;
  in_progress_count: number;
  overdue_count: number;
  due_this_week_count: number;
  filed_this_month_count: number;
  total_fee_pending: string;
  total_fee_billed: string;
  // Assignment-state breakdown (open tasks)
  unassigned_count?: number;
  individual_count?: number;
  team_pool_count?: number;
  team_assigned_count?: number;
  my_team_pool_count?: number;
}

// ============== Compliance Status Report ==============

export interface ComplianceStatusEntry {
  party_id: number;
  party_name: string;
  item_id: number;
  item_name: string;
  item_code?: string | null;
  period_id: string;
  status: string; // 'filed' | 'pending' | 'overdue' | 'no_task'
  filing_due_date?: string | null;
  filed_date?: string | null;
  task_id?: number | null;
  fee_amount?: number | null;
  is_billed: boolean;
  assigned_user_name?: string | null;
}

export interface ComplianceStatusReport {
  items: ComplianceStatusEntry[];
  total: number;
  period_id: string;
}

// ============== Filing Task Extension ==============

export interface ComplianceTaskFields {
  compliance_service_id?: number | null;
  return_type_id?: number | null;
  subscription_id?: number | null;
  period_id?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  filing_due_date?: string | null;
  extended_due_date?: string | null;
  filed_date?: string | null;
  acknowledgment_number?: string | null;
  tax_liability?: string | null;
  tax_paid?: string | null;
  fee_amount?: string | null;
  is_billed?: boolean;
  invoice_voucher_id?: number | null;
  is_auto_generated?: boolean;
}

export interface FileTaskRequest {
  filed_date: string;
  acknowledgment_number?: string | null;
  tax_liability?: string | null;
  tax_paid?: string | null;
}

// ============== Constants ==============

export const COMPLIANCE_CATEGORIES: { value: ComplianceCategory; label: string }[] = [
  { value: 'indirect_tax', label: 'Indirect Tax' },
  { value: 'direct_tax', label: 'Direct Tax' },
  { value: 'compliance', label: 'Compliance' },
];

export const RETURN_FREQUENCIES: { value: ReturnFrequency; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'on_demand', label: 'On Demand' },
];

export const SUBSCRIPTION_STATUSES: { value: SubscriptionStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const BILLING_TYPES: { value: BillingType; label: string }[] = [
  { value: 'per_return', label: 'Per Return' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'retainer', label: 'Retainer' },
];

export const TASK_PRIORITIES: { value: string; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
